import React from 'react';
import { useGameStore } from '../../store/gameStore';
import { Clock, AlertTriangle } from 'lucide-react';
import { GameTooltip } from '../ui/GameTooltip';

/**
 * TurnCounter - Enhanced turn counter with urgency indicators
 * Shows current turn, progress bar, and visual urgency at thresholds
 * @param {boolean} compact - Render in compact mode for mobile/tablet
 */
export const TurnCounter = ({ className = '', compact = false }) => {
  const turnCount = useGameStore((state) => state.turnCount);
  const maxTurns = useGameStore((state) => state.maxTurns) || 30;
  const selectedTheme = useGameStore((state) => state.selectedTheme);

  const turnsRemaining = maxTurns - turnCount;
  const progress = (turnCount / maxTurns) * 100;

  // Determine urgency level
  const getUrgencyLevel = () => {
    if (turnsRemaining <= 5) return 'critical';
    if (turnsRemaining <= 10) return 'warning';
    return 'normal';
  };

  const urgency = getUrgencyLevel();

  const urgencyColors = {
    normal: 'text-green-400',
    warning: 'text-yellow-400',
    critical: 'text-red-400',
  };

  const progressColors = {
    normal: 'bg-green-500',
    warning: 'bg-yellow-500',
    critical: 'bg-red-500',
  };

  const themeAccent = {
    noir: '#DC143C',
    '90s_nyc': '#FF6B35',
    miami_vice: '#FF1493',
  }[selectedTheme || 'noir'];

  const tooltipContent = (
    <div className="text-center">
      <p className="font-medium">{turnsRemaining} turns remaining</p>
      {urgency === 'critical' && (
        <p className="text-xs text-red-400 mt-1">Time is running out!</p>
      )}
      {urgency === 'warning' && (
        <p className="text-xs text-yellow-400 mt-1">Investigation winding down</p>
      )}
    </div>
  );

  return (
    <GameTooltip content={tooltipContent} side="bottom">
      <div
        className={`flex items-center bg-black/40 rounded-lg border border-white/10 ${
          compact ? 'gap-1.5 px-2 py-1.5' : 'gap-2 px-3 py-2'
        } ${className}`}
        data-tutorial="turn-counter"
      >
        {urgency === 'critical' ? (
          <AlertTriangle className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} ${urgencyColors[urgency]} animate-pulse`} />
        ) : (
          <Clock
            className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} ${urgency !== 'normal' ? urgencyColors[urgency] : ''}`}
            style={{ color: urgency === 'normal' ? themeAccent : undefined }}
          />
        )}

        <div className="flex flex-col gap-0.5">
          <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium ${urgencyColors[urgency]}`}>
            {compact ? `${turnCount}/${maxTurns}` : `Turn ${turnCount}/${maxTurns}`}
          </span>

          {/* Progress bar - hide in compact mode */}
          {!compact && (
            <div className="w-16 h-1 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full ${progressColors[urgency]} transition-all duration-300`}
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
      </div>
    </GameTooltip>
  );
};

export default TurnCounter;
